import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/utils/auth";
import axios from "axios";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const addBusinessDays = (startDate, days) => {
  const date = new Date(startDate);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      added += 1;
    }
  }
  return date;
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently, isAuthenticated } = useAuth();
  const [status, setStatus] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !isAuthenticated) {
      setStatus({ loading: false, error: "Missing payment session.", data: null });
      return;
    }

    const verifySession = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email",
          },
        });
        const response = await axios.post(
          `${API}/payments/verify-session`,
          { session_id: sessionId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setStatus({ loading: false, error: "", data: response.data });
      } catch (err) {
        setStatus({ loading: false, error: "Unable to verify payment.", data: null });
      }
    };

    verifySession();
  }, [getAccessTokenSilently, isAuthenticated, searchParams]);

  const createdAt = status.data?.created_at ? new Date(status.data.created_at) : new Date();
  const shipDate = addBusinessDays(createdAt, 10);

  return (
    <div className="min-h-screen px-6 py-16" data-testid="payment-success-page">
      <div className="mx-auto w-full max-w-3xl space-y-6 text-center">
        <h1 className="text-3xl font-bold uppercase" data-testid="payment-success-title">
          Payment successful
        </h1>
        <p className="text-sm text-muted-foreground" data-testid="payment-success-subtitle">
          Thanks for your order. Your payment has been received.
        </p>

        {status.loading ? (
          <p className="text-sm text-muted-foreground" data-testid="payment-success-loading">
            Fetching receipt details...
          </p>
        ) : status.error ? (
          <p className="text-sm text-red-500" data-testid="payment-success-error">
            {status.error}
          </p>
        ) : (
          <div
            className="mx-auto w-full max-w-xl space-y-3 rounded-none border-2 border-black bg-white px-6 py-6 text-left"
            data-testid="payment-success-receipt"
          >
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <span data-testid="payment-success-order-label">Order ID</span>
              <span className="font-semibold text-primary" data-testid="payment-success-order-id">
                {status.data?.order_id?.slice(0, 8).toUpperCase()}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <span data-testid="payment-success-total-label">Total paid</span>
              <span className="font-semibold text-primary" data-testid="payment-success-total">
                ${status.data?.payment_amount?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) || "0.00"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <span data-testid="payment-success-ship-label">Estimated ship date</span>
              <span className="font-semibold text-primary" data-testid="payment-success-ship-date">
                {shipDate.toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        <Link to="/builder" data-testid="payment-success-back-link">
          <Button
            className="h-12 rounded-none border-2 border-black bg-primary px-8 text-xs font-bold uppercase tracking-[0.3em] text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
            data-testid="payment-success-back-button"
          >
            Back to builder
          </Button>
        </Link>
      </div>
    </div>
  );
}
